import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Plus, CheckCircle, XCircle, X, ChevronRight, Pencil, Save, ClipboardList, BadgeCheck, FileSignature } from 'lucide-react';
import { ApplicantsView } from '../components/ApplicantsManager';
import HireSorterPanel from '../components/HireSorterPanel';
import { missingDetailKeys, DETAIL_LABEL } from '../utils/driverDetailFields';
import { periodGrid, todayISO } from '../utils/periods';
import { gstStatusFor, gstHistory } from '../utils/gstStatus';
import DriverAvatar, { fetchDriverPhotos } from '../components/DriverPhoto';

// Apps Script proxy (handles the 302 redirect + CORS). addDriver / updateDriver
// post here — this constant was missing, causing "APPS_SCRIPT_URL is not defined".
const APPS_SCRIPT_URL = '/.netlify/functions/appsscript';
const DETAILS_URL = '/.netlify/functions/driver-details-sb';
// Live contract registers — the Contracts page reads these same endpoints. The
// Personnel "Contract" columns are DERIVED from them, not from a stale sheet flag.
const AGREEMENT_URL = '/.netlify/functions/subcontractor-agreement-sb'; // HPR → subcontractor
const CONTRACT_URL  = '/.netlify/functions/vehicle-contract-sb';        // HPL lease → driver

// Vehicle owner (PHB / HPL / SG Fleet / Owner Driver) → short label + badge colour.
// Every lease is HPL-as-Lessor regardless of who owns the van; the owner is shown
// only to say which entity's vehicle the (HPL) lease covers. Mirrors FleetPage.
const OWNER_SHORT = {
  'PHB': 'PHB', 'PH Bouveng Pty Ltd': 'PHB',
  'HPL': 'HPL', 'Hollypaul Leasing Pty Ltd': 'HPL',
  'SG Fleet': 'SG Fleet',
  'Owner Driver': 'O/D',
};
const OWNER_BADGE = {
  'PHB':      { bg: '#2563eb',         color: '#fff' },
  'HPL':      { bg: 'var(--hp-gold)',  color: '#000' },
  'SG Fleet': { bg: '#7c3aed',         color: '#fff' },
  'O/D':      { bg: 'var(--hp-surface)', color: 'var(--hp-dim)' },
};

// A driver may have several register rows over time (withdrawn + re-issued). Keep
// the most meaningful one: signed beats pending beats withdrawn/declined/void, and
// within a rank the newest wins.
const STATUS_RANK = { signed: 3, pending: 2, declined: 1, withdrawn: 1, void: 0 };
function bestContractByLogin(list) {
  const map = {};
  for (const c of (list || [])) {
    const login = (c.driverLogin || '').toUpperCase();
    if (!login) continue;
    const cur = map[login];
    const r = STATUS_RANK[c.status] ?? 0;
    const cr = cur ? (STATUS_RANK[cur.status] ?? 0) : -1;
    if (!cur || r > cr || (r === cr && (c.createdAt || '') > (cur.createdAt || ''))) map[login] = c;
  }
  return map;
}

// The van a driver currently drives (used to label their lease with its owner).
function vehicleForDriverLogin(login, vehicles) {
  const key = (login || '').toUpperCase();
  if (!key) return null;
  return (vehicles || []).find(v => !v.archived && (v.login || '').toUpperCase() === key) || null;
}

// Small status pill for a contract register row. `contract` is the best register
// row for the driver (or null = nothing on file yet).
function ContractStatusBadge({ contract, loading }) {
  if (loading) return <span style={{ color: 'var(--hp-muted)', fontSize: '0.8rem' }}>…</span>;
  const status = contract?.status || 'none';
  const cfg = {
    signed:    { color: 'var(--hp-green)', icon: <CheckCircle size={13} />, label: 'Signed' },
    pending:   { color: 'var(--hp-amber)', icon: <ClipboardList size={13} />, label: 'Pending' },
    withdrawn: { color: 'var(--hp-muted)', icon: <XCircle size={13} />, label: 'Withdrawn' },
    declined:  { color: 'var(--hp-red)',   icon: <XCircle size={13} />, label: 'Declined' },
    void:      { color: 'var(--hp-muted)', icon: <XCircle size={13} />, label: 'Void' },
    none:      { color: 'var(--hp-red)',   icon: <XCircle size={13} />, label: 'None' },
  }[status] || { color: 'var(--hp-muted)', icon: null, label: status };
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: cfg.color, fontSize: '0.8rem' }}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

// A contract needs (re)generating unless it's live — i.e. anything that isn't
// signed or pending (none / withdrawn / declined / void) can be issued afresh.
const CONTRACT_ACTIVE = new Set(['signed', 'pending']);
const needsGenerate = (c) => !c || !CONTRACT_ACTIVE.has(c.status);

// Small button shown when no live contract is on file — jumps to the Contracts
// page with the tab + driver pre-selected so the build starts there. Labelled
// "Re-issue" when it's replacing a dead (withdrawn/declined/void) contract.
function GenerateContractButton({ onClick, label = 'Generate' }) {
  return (
    <button
      onClick={onClick}
      title="Create this contract on the Contracts page"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: 'none', border: '1px solid var(--hp-gold)', color: 'var(--hp-gold)', fontSize: '0.64rem', fontWeight: 600, padding: '2px 7px', borderRadius: 5, cursor: 'pointer', whiteSpace: 'nowrap' }}
    >
      <FileSignature size={11} /> {label}
    </button>
  );
}

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d) ? '' : d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Read-only contract summary card for the driver profile drawer.
function ContractSummary({ title, contract, loading, owner, rego, noVehicle, onGenerate }) {
  const ownerBadge = owner ? (OWNER_BADGE[owner] || { bg: 'var(--hp-surface)', color: 'var(--hp-dim)' }) : null;
  return (
    <div style={{ padding: '12px 14px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: '.72rem', fontWeight: 600, color: 'var(--hp-dim)' }}>{title}</span>
        {ownerBadge && (
          <span style={{ background: ownerBadge.bg, color: ownerBadge.color, fontSize: '0.6rem', fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>{owner}</span>
        )}
      </div>
      {noVehicle
        ? <span style={{ color: 'var(--hp-muted)', fontSize: '0.8rem' }}>No van assigned</span>
        : owner === 'O/D'
          ? <span style={{ color: 'var(--hp-muted)', fontSize: '0.8rem' }}>Owner driver — no lease</span>
          : <>
              <ContractStatusBadge contract={contract} loading={loading} />
              <div style={{ marginTop: 6, fontSize: '.66rem', color: 'var(--hp-dim)', lineHeight: 1.6 }}>
                {rego && <div>Vehicle: <b>{rego}</b></div>}
                {contract?.createdAt && <div>Sent: {fmtDate(contract.createdAt)}</div>}
                {contract?.signedAt && <div>Signed: {fmtDate(contract.signedAt)}</div>}
                {!loading && needsGenerate(contract) && onGenerate && (
                  <div style={{ marginTop: 6 }}>
                    {!contract && <div style={{ marginBottom: 6 }}>Not yet issued.</div>}
                    <GenerateContractButton label={contract ? 'Re-issue' : 'Generate'} onClick={onGenerate} />
                  </div>
                )}
              </div>
            </>
      }
    </div>
  );
}

// ABN Lookup function
async function lookupABN(abn) {
  const res = await fetch(`/.netlify/functions/abn-lookup?abn=${encodeURIComponent(abn)}`);
  if (!res.ok) throw new Error(`ABN lookup failed: ${res.status}`);
  return await res.json();
}

const GST_STATUS_URL = '/.netlify/functions/gst-status-sb';

// Is this driver GST-registered according to OUR record? The flag arrives as a real
// boolean from listDriversFull but as 'Yes'/'No' from some older paths — treat both.
const gstOnRecord = (d) => d?.gstRegistered === true || d?.gstRegistered === 'Yes';

// ─── GST Check ──────────────────────────────────────────────────────────────
// Checks one driver's ABN against the ABR and, on your say-so, writes the verdict as a
// DATED status (gst-status-sb) that the RCTI reads for the period it is building. The
// write is deliberately two steps: the dated status is what the invoice bills, and the
// live drivers.gst_registered flag is what Personnel/Xero/the profile show. Both are set,
// dated FIRST — see the ordering note in applyGst.
function GstCheckModal({ driver, onClose, onApplied, addNotification }) {
  const [abr, setAbr] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const periods = React.useMemo(() => periodGrid({ back: 6, forward: 1 }), []);
  const [fromISO, setFromISO] = useState(() => (periods.find(p => p.current) || periods[0]).startISO);
  // The driver's DATED history — what the RCTI actually bills. Without it this modal could
  // only compare the live flag to the ABR, and a driver already on dated GST would read as
  // "nothing to change" while every invoice kept billing the old dated status. That is
  // exactly what happened to VALBUENAS (Aug 2026): flag said Yes, ABR said Yes, and the
  // RCTI still billed No because a dated No was in force. The live flag is cosmetic here.
  const [hist, setHist] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await lookupABN(driver.abn || '');
        if (cancelled) return;
        if (r.error) setError(r.error); else setAbr(r);
      } catch (e) { if (!cancelled) setError(e.message); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [driver.abn]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const j = await fetch(GST_STATUS_URL, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'getGstStatus' }),
        }).then(r => r.json());
        if (cancelled) return;
        setHist(j.success ? ((j.gstStatus || {})[(driver.loginName || '').toUpperCase()] || []) : []);
      } catch { if (!cancelled) setHist([]); }
    })();
    return () => { cancelled = true; };
  }, [driver.loginName]);

  const onRecord = gstOnRecord(driver);
  const fromAbr = !!(abr && abr.gst);
  const mismatch = !!abr && onRecord !== fromAbr;
  const abnCancelled = !!abr && abr.status !== 'Active';
  // What the RCTI would bill for the SELECTED period — dated status if there is one, else
  // the live flag (the same fall-through buildRCTI uses).
  const dated = gstStatusFor(hist, fromISO);
  const billsGst = dated ? dated.gstRegistered !== false : onRecord;
  const changes = !!abr && (mismatch || fromAbr !== billsGst);

  const applyGst = async () => {
    const chosen = periods.find(p => p.startISO === fromISO);
    const msg = `Set ${driver.name || driver.loginName} to ${fromAbr ? 'GST REGISTERED' : 'NOT registered for GST'} from ${chosen ? chosen.label : fromISO}?\n\n`
      + `The ABR says ${fromAbr ? 'registered' : 'not registered'}; the record says ${onRecord ? 'registered' : 'not registered'}.\n`
      + `RCTIs for this period onward will ${fromAbr ? 'include' : 'NOT include'} 10% GST.\n\n`
      + `Every period before this one keeps billing exactly as it does today.`;
    if (!window.confirm(msg)) return;
    setApplying(true);
    try {
      // Date the status FIRST: the baseline it snapshots must be the PRE-change flag, and
      // the updateDriver below overwrites it. Reversed, earlier periods would be
      // baselined to the new status — silently re-pricing them, which is the whole thing
      // this feature exists to prevent.
      const res = await fetch(GST_STATUS_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'setGstStatus', login: driver.loginName, effectiveFromISO: fromISO,
          gstRegistered: fromAbr, baselineGstRegistered: onRecord,
          source: 'abr', abr, checkedAt: new Date().toISOString(),
          note: `ABR check: ${abr.entityName || ''}`.trim(),
        }),
      });
      const j = await res.json();
      if (!j.success) throw new Error(j.error || 'Could not save the GST status');

      // Sync the live flag so the profile, Personnel list and Xero agree with the invoice.
      // Non-fatal: the dated status is what the RCTI bills, so a failure here is an
      // inconsistency to report, not a reason to claim the change didn't happen.
      let warn = '';
      try {
        const u = await fetch(APPS_SCRIPT_URL, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'updateDriver', login: driver.loginName, updates: { gstRegistered: fromAbr ? 'Yes' : 'No' } }),
        }).then(r => r.json());
        if (!u.success) warn = u.error || 'unknown error';
      } catch (e) { warn = e.message; }

      onApplied && onApplied({ gstRegistered: fromAbr });
      addNotification(
        warn
          ? `GST dated from ${chosen ? chosen.label : fromISO} — but the profile flag didn't sync (${warn}). The RCTI is correct; fix the flag via Edit.`
          : `${driver.loginName} set to ${fromAbr ? 'GST registered' : 'NOT GST registered'} from ${chosen ? chosen.label : fromISO}`,
        warn ? 'error' : 'success'
      );
      onClose();
    } catch (e) {
      addNotification(`GST update failed: ${e.message}`, 'error');
    } finally { setApplying(false); }
  };

  const box = { padding: '14px 16px', borderRadius: 8, marginBottom: 16 };

  return (
    // This modal opens from INSIDE the profile drawer, which already lays down its own
    // rgba(0,0,0,0.7) backdrop. A second 0.75 on top compounds to ~0.93 and drags the
    // whole page to mud — hence the lighter scrim here.
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 80, overflowY: 'auto' }}>
      <div style={{ width: 520, maxWidth: '95%', background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 12, padding: 28, marginBottom: 40, boxShadow: '0 24px 60px rgba(0,0,0,0.45)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.65rem', textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--hp-gold)', marginBottom: 4 }}>ABR Web Service</div>
            <h3 style={{ margin: 0 }}>GST Status · {driver.loginName}</h3>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        {loading && <div style={{ ...box, background: 'var(--hp-surface)', color: 'var(--hp-dim)', fontSize: '.85rem' }}>Checking ABN {driver.abn} against the ABR…</div>}

        {!loading && error && (
          <div style={{ ...box, background: 'rgba(255,165,0,0.06)', border: '1px solid rgba(255,165,0,0.25)' }}>
            <div style={{ color: 'var(--hp-amber)', fontSize: '.85rem' }}>⚠ Lookup failed: {error}</div>
            <div style={{ color: 'var(--hp-dim)', fontSize: '.75rem', marginTop: 6 }}>Nothing has been changed. The record still says {onRecord ? 'GST registered' : 'not GST registered'}.</div>
          </div>
        )}

        {!loading && abr && (
          <>
            {/* The verdict — the thing you opened this for, so it is a solid block of
                colour with white text rather than a tint. A tinted panel reads as
                decoration and depends on the theme's text colour landing legibly on it;
                this reads the same in both themes and from across the room. */}
            <div style={{ ...box, padding: 0, overflow: 'hidden', border: 'none' }}>
              <div style={{ background: fromAbr ? '#16A34A' : '#DC2626', padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
                {fromAbr ? <CheckCircle size={26} color="#FFFFFF" style={{ flexShrink: 0 }} /> : <XCircle size={26} color="#FFFFFF" style={{ flexShrink: 0 }} />}
                <div>
                  <div style={{ fontWeight: 800, fontSize: '1.15rem', color: '#FFFFFF', lineHeight: 1.25 }}>
                    {fromAbr ? 'Registered for GST' : 'Not registered for GST'}
                  </div>
                  <div style={{ fontSize: '.78rem', color: 'rgba(255,255,255,0.85)', marginTop: 2 }}>
                    According to the ABR{abr.gst ? `, since ${abr.gst}` : ''}
                  </div>
                </div>
              </div>
              <div style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderTop: 'none', padding: '12px 18px', fontSize: '.78rem', color: 'var(--hp-dim)', lineHeight: 1.7 }}>
                <div style={{ color: 'var(--hp-text)', fontWeight: 600 }}>{abr.entityName}</div>
                {abr.entityType ? <div>{abr.entityType}</div> : null}
                <div>
                  ABN {driver.abn} · <strong style={{ color: abnCancelled ? 'var(--hp-danger)' : 'var(--hp-green)' }}>{abr.status}</strong>
                </div>
              </div>
            </div>

            {abnCancelled && (
              <div style={{ ...box, background: 'rgba(220,53,69,0.10)', border: '1px solid rgba(220,53,69,0.4)', color: 'var(--hp-danger)', fontSize: '.82rem' }}>
                ⚠ This ABN is <strong>{abr.status}</strong>, not Active. That is a bigger problem than GST — an invoice to a cancelled ABN is not a valid tax invoice.
              </div>
            )}

            {/* Record vs ABR */}
            <div style={{ ...box, background: 'var(--hp-surface)', border: `1px solid ${mismatch ? 'var(--hp-amber)' : 'var(--hp-border)'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.8rem', marginBottom: 6 }}>
                <span style={{ color: 'var(--hp-dim)' }}>Our record says</span>
                <strong style={{ color: 'var(--hp-text)' }}>{onRecord ? 'Yes' : 'No'}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.8rem' }}>
                <span style={{ color: 'var(--hp-dim)' }}>The ABR says</span>
                <strong style={{ color: mismatch ? 'var(--hp-amber)' : 'var(--hp-text)' }}>{fromAbr ? 'Yes' : 'No'}</strong>
              </div>
              {mismatch && (
                <div style={{ fontSize: '.78rem', color: 'var(--hp-amber)', marginTop: 10, lineHeight: 1.6 }}>
                  ⚠ These disagree. {onRecord
                    ? 'We are paying 10% GST to someone the ABR says is not registered.'
                    : 'We are not paying GST to someone the ABR says is registered.'}
                </div>
              )}
              {!mismatch && <div style={{ fontSize: '.78rem', color: 'var(--hp-green)', marginTop: 10 }}>✓ The record matches the ABR.</div>}
            </div>

            {/* The period selector is NOT gated on a record-vs-ABR mismatch. The flag is
                only what the profile displays; the dated status below is what the invoice
                bills, and the two can disagree — so hiding this whenever the flag happened
                to match the ABR left no way to correct the thing that actually bills. */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Takes effect from</label>
              <select className="form-select" style={inputStyle} value={fromISO} onChange={e => setFromISO(e.target.value)}>
                {periods.map(p => <option key={p.startISO} value={p.startISO}>{p.label}{p.current ? ' — current' : ''}</option>)}
              </select>
              <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginTop: 6, lineHeight: 1.6 }}>
                GST applies to a whole billing period, so this is dated to a period start. Every earlier period keeps the status it bills on today — rebuilding them will not re-price them.
              </div>
            </div>

            {/* What the RCTI bills for the SELECTED period — the number that matters. */}
            <div style={{ ...box, background: 'var(--hp-surface)', border: `1px solid ${changes ? 'var(--hp-amber)' : 'var(--hp-border)'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.8rem' }}>
                <span style={{ color: 'var(--hp-dim)' }}>The RCTI bills this period</span>
                <strong style={{ color: changes ? 'var(--hp-amber)' : 'var(--hp-text)' }}>
                  {billsGst ? 'Yes — 10% GST' : 'No GST'}
                </strong>
              </div>
              <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginTop: 6, lineHeight: 1.6 }}>
                {hist === null ? 'Checking the dated history…'
                  : dated
                    ? `From the dated status set ${dated.baseline ? 'as the pre-change baseline' : `for ${dated.effectiveFromISO}`}${dated.setBy ? ` by ${dated.setBy}` : ''}. The live flag is not read for this driver.`
                    : 'No dated status — this falls through to the live GST Registered flag above.'}
              </div>
              {hist !== null && gstHistory(hist).length > 0 && (
                <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginTop: 8, lineHeight: 1.7, borderTop: '1px solid var(--hp-border)', paddingTop: 8 }}>
                  {gstHistory(hist).map(h => (
                    <div key={h.effectiveFromISO}>
                      {h.effectiveFromISO} → <strong style={{ color: h.gstRegistered ? 'var(--hp-green)' : 'var(--hp-text)' }}>{h.gstRegistered ? 'GST' : 'no GST'}</strong>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {changes ? (
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} disabled={applying} onClick={applyGst}>
                  {applying ? 'Saving…' : `Set GST ${fromAbr ? 'Yes' : 'No'} from this period`}
                </button>
                <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
              </div>
            ) : (
              <div style={{ fontSize: '.78rem', color: 'var(--hp-green)' }}>
                ✓ Nothing to change for this period — the invoice already bills {billsGst ? 'GST' : 'no GST'}, matching the ABR.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── trading entity ────────────────────────────────────────────────────────────
// An ABN is required; a company is not. A sole trader mid-setup is a normal, tolerated
// state — so it reads as a plain fact, not a fault. No ABN at all is the fault: the
// RCTI needs one.
const ENTITY_URL = '/.netlify/functions/entity-status-sb';
const ENTITY_LOOK = {
  company:     { label: 'Company',      cls: 'badge-green' },
  sole_trader: { label: 'Sole trader',  cls: 'badge-blue'  },
  trust:       { label: 'Trust',        cls: 'badge-blue'  },
  partnership: { label: 'Partnership',  cls: 'badge-blue'  },
  other:       { label: 'Other entity', cls: 'badge-dim'   },
  none:        { label: 'No ABN',       cls: 'badge-red'   },
  unknown:     { label: 'Not checked',  cls: 'badge-dim'   },
};
// What to show before anyone has run a check: an ABN we hold but haven't verified is
// "not checked"; no ABN at all needs no lookup to be a problem.
function entityFor(driver, entities) {
  const e = entities[(driver.loginName || '').toUpperCase()];
  const hasAbn = (driver.abn || '').replace(/\s/g, '').length >= 11;
  if (!e) return { kind: hasAbn ? 'unknown' : 'none', companyPending: false, stale: true };
  if (e.kind === 'unknown' && !hasAbn) return { ...e, kind: 'none' };
  return e;
}

const EMPTY_DRIVER = {
  login: '', name: '', pin: '', address: '', mobile: '', email: '',
  companyName: '', abn: '', accountName: '', bsb: '', accNumber: '', gstRegistered: 'Yes',
  route: 'City', type: 'Parcel', vehicleType: 'Own Vehicle',
  leaseFee: '', leaseRate: '', leaseCap: '',
  indoorStart: '', indoorEnd: '',
  rateParcel: '', rateExp: '', rego: '',
  performsDepotBags: false, performsDOE: false,
};

const labelStyle = {
  fontSize: '.72rem', color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)',
  textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 5,
};
const inputStyle = { color: 'var(--hp-text)', background: 'var(--hp-surface)' };

function SectionTitle({ n, title }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, marginTop: n > 1 ? 20 : 0 }}>
      <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--hp-gold)', color: '#000', fontSize: '.65rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{n}</div>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--hp-gold)', fontWeight: 700 }}>{title}</span>
    </div>
  );
}

// ─── Add Driver Panel ───────────────────────────────────────────────────────

function AddDriverPanel({ onClose, onSuccess, addNotification, initial, findPerson, onUseExisting, onOpenExisting }) {
  const [form, setForm] = useState(initial ? { ...EMPTY_DRIVER, ...initial } : EMPTY_DRIVER);
  const [saving, setSaving] = useState(false);
  // "Driver already exists" used to be a red toast and nothing else — with the new
  // driver missing from the list, it read as "he's in the system and I can't find him".
  // Say who holds the login and give the two ways forward instead.
  const [existing, setExisting] = useState(null);   // { login, name }

  const set = (field, value) => setForm(prev => {
    const next = { ...prev, [field]: value };
    // Retiring someone is the moment the leaving date is known, and it is the only moment
    // anyone is looking at this form. Left to fill itself in later the field stays empty —
    // which is exactly how Weng's departure ended up recorded nowhere but a note. Prefill
    // today and let it be corrected: the date is editable and only ever set when blank, so
    // reopening a driver retired months ago never rewrites the real date.
    if (field === 'active' && value === 'No' && !prev.endDate) next.endDate = todayISO();
    return next;
  });

  const handleSubmit = async () => {
    if (!form.login) { addNotification('Login is required', 'error'); return; }
    if (!form.name)  { addNotification('Name is required', 'error'); return; }
    setSaving(true);
    try {
      const res = await fetch(APPS_SCRIPT_URL, {
        method: 'POST',
        body: JSON.stringify({ action: 'addDriver', driver: { ...form, login: form.login.toUpperCase() } }),
      });
      const data = await res.json();
      if (data.success) {
        addNotification(
          `${form.login.toUpperCase()} added to Drivers tab` +
          (data.pin ? ` — portal PIN ${data.pin} (give this to the driver)` : '') +
          (data.networkPrincipal ? ` · linked to ${data.networkPrincipal} network group` : ''),
          'success'
        );
        onSuccess(form.login.toUpperCase());
        onClose();
      } else if (/already exists/i.test(data.error || '')) {
        const login = form.login.toUpperCase();
        const p = findPerson ? findPerson(login) : null;
        setExisting({ login, name: (p && p.name) || '' });
      } else {
        addNotification(`Failed: ${data.error}`, 'error');
      }
    } catch (err) {
      addNotification(`Error: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ width: 520, background: 'var(--hp-bg)', borderLeft: '1px solid var(--hp-border)', height: '100%', overflowY: 'auto', padding: 28 }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.65rem', textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--hp-gold)', marginBottom: 4 }}>Personnel</div>
            <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Add New Driver</h3>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        {existing && (
          <div style={{ marginBottom: 16, padding: '12px 14px', borderRadius: 8, background: 'rgba(245,158,11,0.10)', border: '1px solid var(--hp-amber, #d97706)', fontSize: '.83rem' }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{existing.login} is already a driver{existing.name ? ` — ${existing.name}` : ''}.</div>
            <div style={{ color: 'var(--hp-muted)', marginBottom: 10 }}>
              Nothing was changed. If this is the same person, the record already exists — use it rather than making a second one.
              If it's someone else, change the Login ID above.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {onUseExisting && (
                <button className="btn btn-primary btn-sm" onClick={() => { onUseExisting(existing.login); onClose(); }}>
                  This is him — link the applicant to {existing.login}
                </button>
              )}
              {onOpenExisting && (
                <button className="btn btn-ghost btn-sm" onClick={() => { onOpenExisting(existing.login); onClose(); }}>
                  Open {existing.login}'s record
                </button>
              )}
            </div>
          </div>
        )}

        <SectionTitle n={1} title="Personal Details" />
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 4 }}>
          <div>
            <label style={labelStyle}>Login ID *</label>
            <input className="form-input" style={inputStyle} placeholder="e.g. SMITHJ" value={form.login} onChange={e => { setExisting(null); set('login', e.target.value.toUpperCase()); }} />
            <div style={{ fontSize: '.65rem', color: 'var(--hp-dim)', marginTop: 3 }}>Uppercase — must match scanner login</div>
          </div>
          <div>
            <label style={labelStyle}>Full Name *</label>
            <input className="form-input" style={inputStyle} placeholder="e.g. John Smith" value={form.name} onChange={e => set('name', e.target.value)} />
          </div>
        </div>
        <div style={{ marginBottom: 4, marginTop: 12 }}>
          <label style={labelStyle}>Address</label>
          <input className="form-input" style={inputStyle} placeholder="Street, Suburb, State, Postcode" value={form.address} onChange={e => set('address', e.target.value)} />
        </div>
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
          <div>
            <label style={labelStyle}>Mobile</label>
            <input className="form-input" style={inputStyle} placeholder="04xx xxx xxx" value={form.mobile} onChange={e => set('mobile', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Email</label>
            <input className="form-input" style={inputStyle} placeholder="email@example.com" value={form.email} onChange={e => set('email', e.target.value)} />
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={labelStyle}>Portal PIN</label>
          <input className="form-input" style={{ ...inputStyle, maxWidth: 200 }} inputMode="numeric" placeholder="Leave blank to auto-generate" value={form.pin} onChange={e => set('pin', e.target.value.replace(/[^0-9]/g, ''))} />
          <div style={{ fontSize: '.65rem', color: 'var(--hp-dim)', marginTop: 3 }}>Set to the driver's scanner PIN for continuity, or leave blank to auto-generate.</div>
        </div>

        <SectionTitle n={2} title="Company & Banking" />
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Company Name</label>
          <input className="form-input" style={inputStyle} placeholder="e.g. Smith Transport Pty Ltd" value={form.companyName} onChange={e => set('companyName', e.target.value)} />
        </div>
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle}>ABN</label>
            <input className="form-input" style={inputStyle} placeholder="xx xxx xxx xxx" value={form.abn} onChange={e => set('abn', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>GST Registered</label>
            <select className="form-select" style={inputStyle} value={form.gstRegistered} onChange={e => set('gstRegistered', e.target.value)}>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={labelStyle}>Account Name</label>
          <input className="form-input" style={inputStyle} placeholder="Bank account name" value={form.accountName} onChange={e => set('accountName', e.target.value)} />
        </div>
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
          <div>
            <label style={labelStyle}>BSB</label>
            <input className="form-input" style={inputStyle} placeholder="xxxxxx" value={form.bsb} onChange={e => set('bsb', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Account Number</label>
            <input className="form-input" style={inputStyle} placeholder="Account number" value={form.accNumber} onChange={e => set('accNumber', e.target.value)} />
          </div>
        </div>

        <SectionTitle n={3} title="Route & Vehicle" />
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Route</label>
            <select className="form-select" style={inputStyle} value={form.route} onChange={e => set('route', e.target.value)}>
              <option>City</option>
              <option>Broadway</option>
              <option>Darlinghurst</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Type</label>
            <select className="form-select" style={inputStyle} value={form.type} onChange={e => set('type', e.target.value)}>
              <option value="Parcel">Parcel</option>
              <option value="Hourly">Hourly</option>
              <option value="None">None</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Vehicle</label>
            <select className="form-select" style={inputStyle} value={form.vehicleType} onChange={e => set('vehicleType', e.target.value)}>
              <option>Own Vehicle</option>
              <option>HPL Leased</option>
            </select>
          </div>
        </div>
        {form.vehicleType === 'HPL Leased' && (
          <div style={{ background: 'rgba(201,168,76,.06)', border: '1px solid rgba(201,168,76,.2)', borderRadius: 8, padding: '12px 14px', marginBottom: 12 }}>
            <div style={{ fontSize: '.72rem', color: 'var(--hp-gold)', marginBottom: 10, fontWeight: 600 }}>HPL Lease Details</div>
            <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <div>
                <label style={labelStyle}>Lease Fee ($)</label>
                <input className="form-input" style={inputStyle} type="number" step="0.01" placeholder="954.55" value={form.leaseFee} onChange={e => set('leaseFee', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Lease Rate (%)</label>
                <input className="form-input" style={inputStyle} type="number" step="0.01" placeholder="0.15" value={form.leaseRate} onChange={e => set('leaseRate', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Lease Cap ($)</label>
                <input className="form-input" style={inputStyle} type="number" step="0.01" placeholder="1050" value={form.leaseCap} onChange={e => set('leaseCap', e.target.value)} />
              </div>
            </div>
            <div style={{ fontSize: '.65rem', color: 'var(--hp-dim)', marginTop: 6 }}>Leave Lease Rate and Cap blank for fixed fee only</div>
          </div>
        )}
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle}>Indoor Start</label>
            <input className="form-input" style={inputStyle} placeholder="e.g. 06:30" value={form.indoorStart} onChange={e => set('indoorStart', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Indoor End</label>
            <input className="form-input" style={inputStyle} placeholder="e.g. 08:30" value={form.indoorEnd} onChange={e => set('indoorEnd', e.target.value)} />
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={labelStyle}>Vehicle Registration</label>
          <input className="form-input" style={inputStyle} placeholder="e.g. ABC123" value={form.rego} onChange={e => set('rego', e.target.value.toUpperCase())} />
        </div>

        <SectionTitle n={4} title="Service Rates" />
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div>
            <label style={labelStyle}>Parcel Rate ($ ex GST)</label>
            <input className="form-input" style={inputStyle} type="number" step="0.01" placeholder="e.g. 2.00" value={form.rateParcel} onChange={e => set('rateParcel', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Express Rate ($ ex GST)</label>
            <input className="form-input" style={inputStyle} type="number" step="0.01" placeholder="e.g. 3.64" value={form.rateExp} onChange={e => set('rateExp', e.target.value)} />
          </div>
        </div>
        <div style={{ background: 'var(--hp-surface)', borderRadius: 8, padding: '12px 14px', marginBottom: 6 }}>
          <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginBottom: 10 }}>Additional services — fixed rate $4.55 each</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { field: 'performsDepotBags', label: 'Depot Bags', desc: 'Driver performs depot bag deliveries' },
              { field: 'performsDOE', label: 'DOE', desc: 'Driver performs Dept of Education deliveries' },
            ].map(({ field, label, desc }) => (
              <label key={field} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={form[field]} onChange={e => set(field, e.target.checked)}
                  style={{ width: 15, height: 15, accentColor: 'var(--hp-gold)' }} />
                <div>
                  <div style={{ fontSize: '.8rem', fontWeight: 600 }}>{label} <span style={{ color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)', fontSize: '.72rem' }}>$4.55</span></div>
                  <div style={{ fontSize: '.68rem', color: 'var(--hp-dim)' }}>{desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--hp-border)', paddingTop: 16, marginTop: 20, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : 'Add Driver to Sheets'}
          </button>
        </div>

      </div>
    </div>
  );
}

// ─── Profile field sub-components (must be top-level to avoid remount on keystroke) ─

const readOnlyBox = {
  fontSize: '.88rem',
  padding: '8px 10px',
  background: 'var(--hp-surface)',
  border: '1px solid var(--hp-border)',
  borderRadius: 6,
  color: 'var(--hp-text)',
  minHeight: 36,
  display: 'flex',
  alignItems: 'center',
};

function ProfileField({ label, value, field, type = 'text', readOnly, editing, form, set }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      {editing && !readOnly ? (
        <input
          className="form-input"
          style={inputStyle}
          type={type}
          value={form[field] ?? value ?? ''}
          onChange={e => set(field, e.target.value)}
        />
      ) : (
        <div style={{ ...readOnlyBox, color: value ? 'var(--hp-text)' : 'var(--hp-muted)' }}>
          {value || '—'}
        </div>
      )}
    </div>
  );
}

function ProfileSelect({ label, field, options, editing, form, set }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      {editing ? (
        <select className="form-select" style={inputStyle} value={form[field]} onChange={e => set(field, e.target.value)}>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <div style={readOnlyBox}>{form[field] || '—'}</div>
      )}
    </div>
  );
}

// The Vehicle field is the wording printed on the driver's paperwork, so it was free
// text — and drifted ("seperate commercial agreement", "a seperate lease agreement").
// These are the two standard wordings, the same pair onboarding writes (VEHICLE_MAP in
// drivers-sb.js / Code.js). A driver still on an old one-off wording keeps it as an
// option until someone picks a standard one; nothing rewrites it behind their back.
const VEHICLE_OPTIONS = [
  { value: 'Vehicle supplied under separate commercial hire agreement where applicable.', label: 'Leases a van from us (HPL)' },
  { value: 'I supply my own vehicle and running costs', label: 'Own vehicle' },
];

function VehicleSelect({ editing, form, set }) {
  const current = form.vehicle || '';
  const known = VEHICLE_OPTIONS.find(o => o.value === current);
  return (
    <div>
      <label style={labelStyle}>Vehicle</label>
      {editing ? (
        <select className="form-select" style={inputStyle} value={current} onChange={e => set('vehicle', e.target.value)}>
          {!current && <option value="">Choose…</option>}
          {VEHICLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          {current && !known && <option value={current}>As recorded: {current}</option>}
        </select>
      ) : (
        <div style={{ ...readOnlyBox, color: current ? 'var(--hp-text)' : 'var(--hp-muted)' }}>
          {known ? known.label : (current || '—')}
        </div>
      )}
      {current && (
        <div style={{ fontSize: '.68rem', color: 'var(--hp-dim)', lineHeight: 1.5, marginTop: 4 }}>“{current}”</div>
      )}
      {current === VEHICLE_OPTIONS[0].value && !(Number(form.leaseFee) > 0) && !(Number(form.leaseRate) > 0) && (
        <div style={{ fontSize: '.68rem', color: 'var(--hp-amber)', lineHeight: 1.5, marginTop: 4 }}>
          ⚠ No lease fee set below, so the RCTI deducts nothing for the van.
        </div>
      )}
    </div>
  );
}

// ─── Trading entity card ──────────────────────────────────────────────────────
// Sole trader or company, taken from the ABR, plus the human half: whether a company
// is being set up and by when. The two halves are kept apart on purpose — a re-check
// refreshes what the ABR says and never argues with what a person recorded.
function EntityCard({ driver, entity, addNotification, onChanged }) {
  const [busy, setBusy] = useState('');
  const [pending, setPending] = useState(!!entity.companyPending);
  const [expected, setExpected] = useState(entity.companyExpectedBy || '');
  const [note, setNote] = useState(entity.note || '');
  const hasAbn = (driver.abn || '').replace(/\s/g, '').length >= 11;
  const look = ENTITY_LOOK[entity.kind] || ENTITY_LOOK.unknown;
  const dirty = pending !== !!entity.companyPending
    || expected !== (entity.companyExpectedBy || '')
    || note !== (entity.note || '');

  const call = async (body) => {
    const d = await fetch(ENTITY_URL, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }).then(r => r.json());
    if (!d || !d.success) throw new Error((d && d.error) || 'Request failed');
    return d;
  };

  const check = async () => {
    setBusy('check');
    try {
      const abr = await lookupABN(driver.abn);
      if (abr.error) throw new Error(abr.error);
      await call({ action: 'saveEntityCheck', login: driver.loginName, abn: driver.abn, abr });
      addNotification(`${abr.entityName || driver.loginName} — ${abr.entityType || 'entity type not returned'}`, 'success');
      onChanged();
    } catch (e) { addNotification(e.message, 'error'); } finally { setBusy(''); }
  };

  const save = async () => {
    setBusy('save');
    try {
      await call({ action: 'setEntity', login: driver.loginName, companyPending: pending, companyExpectedBy: expected, note });
      addNotification('Saved', 'success');
      onChanged();
    } catch (e) { addNotification(e.message, 'error'); } finally { setBusy(''); }
  };

  return (
    <div style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 8, padding: '12px 14px', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap', marginBottom: 8 }}>
        <span className={`badge ${look.cls}`}>{look.label}</span>
        {entity.entityName && <span style={{ fontSize: '.8rem' }}>{entity.entityName}</span>}
        {entity.abnStatus && entity.abnStatus !== 'Active' && (
          <span className="badge badge-red">ABN {entity.abnStatus}</span>
        )}
        <span style={{ flex: 1 }} />
        <button className="btn btn-secondary btn-sm" disabled={!hasAbn || busy === 'check'} onClick={check}
          title={hasAbn ? 'Look this ABN up on the ABR' : 'No ABN on the record to look up'}>
          {busy === 'check' ? 'Checking…' : entity.checkedAt ? 'Re-check' : 'Check the ABR'}
        </button>
      </div>

      <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginBottom: 10 }}>
        {!hasAbn
          ? 'No ABN on this record. An ABN is required — the RCTI cannot be made out without one.'
          : entity.checkedAt
            ? `${entity.entityType || 'Entity type unknown'} · checked ${new Date(entity.checkedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`
            : 'Not checked against the ABR yet.'}
      </div>

      {/* A company is not required to start — but if one is coming, it is a
          transition somebody has to finish, so it gets a date. */}
      {entity.kind !== 'company' && (
        <>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 8 }}>
            <input type="checkbox" checked={pending} onChange={e => setPending(e.target.checked)}
              style={{ width: 15, height: 15, accentColor: 'var(--hp-gold)' }} />
            <span style={{ fontSize: '.8rem' }}>A company is being set up</span>
          </label>
          {pending && (
            <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10, marginBottom: 8 }}>
              <div>
                <label style={labelStyle}>Expected by</label>
                <input className="form-input" style={inputStyle} type="date" value={expected} onChange={e => setExpected(e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Note</label>
                <input className="form-input" style={inputStyle} placeholder="e.g. accountant lodging this week" value={note} onChange={e => setNote(e.target.value)} />
              </div>
            </div>
          )}
          {pending && (
            <div style={{ fontSize: '.68rem', color: 'var(--hp-dim)', marginBottom: 8 }}>
              When the company exists: update the company name and ABN here, generate a fresh
              subcontractor agreement in the company's name, and change the bank account — between
              pay periods, because an RCTI is made out to whichever entity is on the record when it is built.
            </div>
          )}
          {dirty && (
            <button className="btn btn-primary btn-sm" disabled={busy === 'save'} onClick={save}>
              {busy === 'save' ? 'Saving…' : 'Save'}
            </button>
          )}
        </>
      )}
      {entity.kind === 'company' && entity.companyArrivedAt && (
        <div style={{ fontSize: '.72rem', color: 'var(--hp-green)' }}>
          Moved from a sole-trader ABN to a company — recorded {new Date(entity.companyArrivedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}.
        </div>
      )}
    </div>
  );
}

// ─── Driver Profile Panel ───────────────────────────────────────────────────

function DriverProfilePanel({ driver, subcon, lease, vehicle, entity, contractsLoading, onClose, addNotification, onSaved, onEntityChanged }) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: driver.name || '',
    address: driver.address || '',
    phone: driver.phone || '',
    email: driver.email || '',
    companyName: driver.companyName || '',
    abn: driver.abn || '',
    gstRegistered: driver.gstRegistered ? 'Yes' : 'No',
    accountName: driver.accountName || '',
    bsb: driver.bsb || '',
    accNumber: driver.accNumber || '',
    // There was no way to retire a driver anywhere in the app — leavers stayed active
    // forever, which keeps them in the RCTI build (sheets.js filters on `active`) and in
    // every picker and headcount. Editable here, admin-only: the portal's self-service
    // field list deliberately excludes it.
    active: driver.status === 'active' ? 'Yes' : 'No',
    // When they left. Blank for everyone still here; see the migration for why an exit
    // needed a date of its own rather than living in a Log-On Check note.
    endDate: driver.endDate || '',
    type: driver.type || 'Parcel',
    vehicle: driver.vehicle || '',
    indoorStart: driver.indoorStart || '',
    indoorEnd: driver.indoorEnd || '',
    // The AP uniform order behind this driver's kit — ordered on AP's supply-chain portal,
    // which hands back an order reference and nothing else. Kept here so "where are my
    // shorts?" and "has this one already been ordered for?" have an answer in the app.
    uniformOrderDate: driver.uniformOrderDate || '',
    uniformOrderRef: driver.uniformOrderRef || '',
    // Lease terms were add-only until now — an existing driver's fee could not be changed
    // anywhere in the app. 0 → '' so an unset fee shows blank, not a misleading "0".
    leaseFee: driver.leaseFee ? String(driver.leaseFee) : '',
    leaseRate: driver.leaseRate ? String(driver.leaseRate) : '',
    leaseCap: driver.leaseCap ? String(driver.leaseCap) : '',
  });

  const set = (field, value) => setForm(prev => {
    const next = { ...prev, [field]: value };
    // Retiring someone is the moment the leaving date is known, and it is the only moment
    // anyone is looking at this form. Left to fill itself in later the field stays empty —
    // which is exactly how Weng's departure ended up recorded nowhere but a note. Prefill
    // today and let it be corrected: the date is editable and only ever set when blank, so
    // reopening a driver retired months ago never rewrites the real date.
    if (field === 'active' && value === 'No' && !prev.endDate) next.endDate = todayISO();
    return next;
  });

  // Unsaved-changes tracking: compare the form to the last-saved baseline.
  const baseline = useRef(form);
  const dirty = editing && JSON.stringify(form) !== JSON.stringify(baseline.current);
  useEffect(() => {
    if (!dirty) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(APPS_SCRIPT_URL, {
        method: 'POST',
        body: JSON.stringify({ action: 'updateDriver', login: driver.loginName, updates: form }),
      });
      const data = await res.json();
      if (data.success) {
        // The backend saves what it can and says what it couldn't (an unapplied migration
        // drops the end date). Surfacing it here is the difference between "the date won't
        // stick" reading as a bug and reading as a migration still to run.
        if (data.warning) addNotification(data.warning, 'warning');
        addNotification(`${driver.loginName} profile updated`, 'success');
        baseline.current = form; // new clean baseline → no longer dirty
        setEditing(false);
        // The sheet is written (name → Contact col), but the cached personnel list
        // isn't — without this the header/list/reopen revert to the old value.
        // Push the saved fields back up so the change shows without a full reload.
        // The lease fields are strings in the form but feed RCTI money arithmetic — coerce
        // so the in-memory patch matches the shape listDriversFull returns on reload.
        onSaved && onSaved({
          ...form,
          gstRegistered: form.gstRegistered === 'Yes',
          // The list keys off `status`, not the form's Yes/No — without this the badge and
          // the active/inactive filter would still show a retired driver as active.
          status: form.active === 'Yes' ? 'active' : 'inactive',
          active: form.active === 'Yes',
          endDate: form.endDate || '',
          uniformOrderDate: form.uniformOrderDate || '',
          uniformOrderRef: form.uniformOrderRef || '',
          leaseFee: Number(form.leaseFee) || 0,
          leaseRate: Number(form.leaseRate) || 0,
          leaseCap: Number(form.leaseCap) || 0,
        });
      } else {
        addNotification(`Failed: ${data.error}`, 'error');
      }
    } catch (err) {
      addNotification(`Error: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const [requesting, setRequesting] = useState(false);
  const requestDetails = async () => {
    const fields = missingDetailKeys(driver);
    if (!fields.length) { addNotification('Nothing missing — all personal details are on file', 'info'); return; }
    if (!window.confirm(`Ask ${driver.name || driver.loginName} to complete: ${fields.map(k => DETAIL_LABEL[k]).join(', ')}?`)) return;
    setRequesting(true);
    try {
      const res = await fetch(DETAILS_URL, {
        method: 'POST',
        body: JSON.stringify({ action: 'requestDetails', login: driver.loginName, name: driver.name || '', fields, note: 'Please complete your details to finish your setup.' }),
      }).then(r => r.json());
      if (res.success) addNotification(`Detail request sent to ${driver.name || driver.loginName} — they'll see it in their portal`, 'success');
      else addNotification(`Request failed: ${res.error || 'unknown error'}`, 'error');
    } catch (e) { addNotification(`Error: ${e.message}`, 'error'); } finally { setRequesting(false); }
  };

  const [checkingGst, setCheckingGst] = useState(false);
  const hasAbn = (driver.abn || '').replace(/\s/g, '').length >= 11;

  const avatarInitials = (driver.name || driver.loginName || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const fp = { editing, form, set };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
      {checkingGst && (
        <GstCheckModal
          driver={driver}
          addNotification={addNotification}
          onClose={() => setCheckingGst(false)}
          onApplied={({ gstRegistered }) => {
            // Keep the open panel honest: the form drives the GST select, and onSaved
            // patches the cached personnel list behind it.
            set('gstRegistered', gstRegistered ? 'Yes' : 'No');
            baseline.current = { ...baseline.current, gstRegistered: gstRegistered ? 'Yes' : 'No' };
            onSaved && onSaved({ gstRegistered });
          }}
        />
      )}
      <div style={{ width: 600, background: 'var(--hp-bg)', borderLeft: '1px solid var(--hp-border)', height: '100%', overflowY: 'auto', padding: 32 }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(201,168,76,0.2), rgba(201,168,76,0.06))', border: '1px solid rgba(201,168,76,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', fontWeight: 700, color: 'var(--hp-gold)', flexShrink: 0 }}>
              {avatarInitials}
            </div>
            <div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.65rem', textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--hp-gold)', marginBottom: 2 }}>
                {driver.loginName}
              </div>
              <h3 style={{ margin: 0, fontSize: '1.1rem', lineHeight: 1.2 }}>{driver.name || driver.loginName}</h3>
              {driver.companyName && (
                <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)', marginTop: 2 }}>{driver.companyName}</div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {!editing ? (
              <>
                {hasAbn && (
                  <button className="btn btn-secondary btn-sm" onClick={() => setCheckingGst(true)} title="Check this ABN's GST registration against the ABR">
                    <BadgeCheck size={13} /> Check GST
                  </button>
                )}
                <button className="btn btn-secondary btn-sm" onClick={requestDetails} disabled={requesting} title="Ask the driver to complete missing personal details in their portal">
                  <ClipboardList size={13} /> {requesting ? 'Sending…' : 'Request details'}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>
                  <Pencil size={13} /> Edit
                </button>
              </>
            ) : (
              <>
                {dirty && <span style={{ alignSelf: 'center', fontSize: '.68rem', fontWeight: 700, color: 'var(--hp-amber)' }}>● Unsaved</span>}
                <button className="btn btn-ghost btn-sm" onClick={() => { setForm(baseline.current); setEditing(false); }}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !dirty}>
                  <Save size={13} /> {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            )}
            <button className="btn btn-ghost btn-sm" onClick={() => { if (!dirty || window.confirm('You have unsaved changes. Discard them?')) onClose(); }}><X size={16} /></button>
          </div>
        </div>

        {/* Status badges */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
          <span className={`badge ${driver.status === 'active' ? 'badge-green' : 'badge-dim'}`} style={{ textTransform: 'capitalize' }}>{driver.status}</span>
          {driver.status !== 'active' && driver.endDate && (
            <span style={{ fontSize: '.72rem', color: 'var(--hp-dim)', alignSelf: 'center' }}>
              left {new Date(driver.endDate + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          )}
          <span className={`badge ${(driver.type || '').trim() === 'Hourly' ? 'badge-blue' : (driver.type || '').trim() === 'Parcel' ? 'badge-green' : 'badge-dim'}`}>
            {(driver.type || 'None').trim()}
          </span>
          {driver.abn && <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.72rem', color: 'var(--hp-dim)', padding: '2px 8px', background: 'var(--hp-surface)', borderRadius: 4 }}>ABN {driver.abn}</span>}
        </div>

        {/* Section 1: Personal */}
        <SectionTitle n={1} title="Personal Details" />
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <ProfileField label="Login ID" value={driver.loginName} readOnly {...fp} />
          <ProfileField label="Full Name" field="name" value={form.name} {...fp} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <ProfileField label="Address" field="address" value={form.address} {...fp} />
        </div>
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <ProfileField label="Mobile" field="phone" value={form.phone} {...fp} />
          <ProfileField label="Email" field="email" value={form.email} type="email" {...fp} />
        </div>

        {/* Section 2: Company & Banking */}
        <SectionTitle n={2} title="Company & Banking" />
        <div style={{ marginBottom: 12 }}>
          <ProfileField label="Company Name" field="companyName" value={form.companyName} {...fp} />
        </div>
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <ProfileField label="ABN" field="abn" value={form.abn} {...fp} />
          <ProfileSelect label="GST Registered" field="gstRegistered" options={['Yes', 'No']} {...fp} />
        </div>
        <EntityCard driver={driver} entity={entity || { kind: 'unknown' }} addNotification={addNotification}
          onChanged={onEntityChanged} />
        <div style={{ marginBottom: 12 }}>
          <ProfileField label="Account Name" field="accountName" value={form.accountName} {...fp} />
        </div>
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <ProfileField label="BSB" field="bsb" value={form.bsb} {...fp} />
          <ProfileField label="Account Number" field="accNumber" value={form.accNumber} {...fp} />
        </div>

        {/* Section 3: Operations */}
        <SectionTitle n={3} title="Operations" />
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <ProfileSelect label="Type" field="type" options={['Parcel', 'Hourly', 'None']} {...fp} />
          <VehicleSelect {...fp} />
        </div>
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <ProfileSelect label="Active" field="active" options={['Yes', 'No']} {...fp} />
          <ProfileField label="End Date" field="endDate" value={form.endDate} type="date" {...fp} />
        </div>
        <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', lineHeight: 1.5, marginBottom: 12 }}>
          {form.active === 'No'
            ? 'Retired — out of the RCTI build, the rosters, the broadcasts and the driver portal, so they can never be invoiced or signed in again. Their history is kept.'
            : 'Set to No when someone leaves, or they stay billable. The end date is their last day.'}
        </div>
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <ProfileField label="Indoor Start" field="indoorStart" value={form.indoorStart} {...fp} />
          <ProfileField label="Indoor End" field="indoorEnd" value={form.indoorEnd} {...fp} />
        </div>

        {/* Uniform — the AP order, so a driver's kit has a date and a reference against it.
            One order per driver (the latest); repeat orders would need a register of their own. */}
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
          <ProfileField label="Uniform Order Date" field="uniformOrderDate" value={form.uniformOrderDate} type="date" {...fp} />
          <ProfileField label="Uniform Order Ref" field="uniformOrderRef" value={form.uniformOrderRef} {...fp} />
        </div>
        <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', lineHeight: 1.5, marginTop: 6 }}>
          The reference AP's supply-chain portal gives back when the uniform order is submitted.
        </div>

        {/* Contracts — read-only mirror of the live registers (edit/sign on the Contracts page). */}
        <SectionTitle n={4} title="Contracts" />
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <ContractSummary
            title="Subcontractor Agreement"
            contract={subcon}
            loading={contractsLoading}
            onGenerate={() => navigate(`/contracts?tab=subcontractor&driver=${encodeURIComponent(driver.loginName || '')}`)}
          />
          <ContractSummary
            title="Vehicle Lease (HPL)"
            contract={lease}
            loading={contractsLoading}
            owner={vehicle ? (OWNER_SHORT[vehicle.owner] || vehicle.owner) : ''}
            rego={vehicle?.rego}
            noVehicle={!vehicle}
            onGenerate={() => navigate(`/contracts?tab=vehicle-lease&driver=${encodeURIComponent(driver.loginName || '')}`)}
          />
        </div>

        {/* HPL lease terms. buildRCTI: `if (leaseRate > 0) … else if (leaseFee > 0)` — the
            RATE WINS and the fixed fee is ignored entirely, so a fee set while a rate is
            present silently does nothing. The warning below makes that visible rather than
            leaving it to be discovered on an invoice. Deducted from gross BEFORE GST → the
            fee is ex GST. */}
        <div style={{ marginTop: 12, padding: '12px 14px', background: 'rgba(201,168,76,.06)', border: '1px solid rgba(201,168,76,.2)', borderRadius: 8 }}>
          <div style={{ fontSize: '.72rem', color: 'var(--hp-gold)', marginBottom: 10, fontWeight: 600 }}>HPL Lease Details</div>
          <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <ProfileField label="Lease Fee ($ ex GST)" field="leaseFee" value={form.leaseFee} type="number" {...fp} />
            <ProfileField label="Lease Rate (%)" field="leaseRate" value={form.leaseRate} type="number" {...fp} />
            <ProfileField label="Lease Cap ($)" field="leaseCap" value={form.leaseCap} type="number" {...fp} />
          </div>
          {Number(form.leaseRate) > 0 && Number(form.leaseFee) > 0 && (
            <div style={{ fontSize: '.68rem', color: 'var(--hp-amber)', marginTop: 8, lineHeight: 1.5 }}>
              ⚠ Lease Rate is set, so the RCTI charges {(Number(form.leaseRate) * 100).toFixed(1)}% of gross
              {Number(form.leaseCap) > 0 ? ` (capped at $${Number(form.leaseCap).toLocaleString('en-AU')})` : ''} and
              <b> ignores the ${Number(form.leaseFee).toLocaleString('en-AU', { minimumFractionDigits: 2 })} fixed fee</b>.
              Clear Lease Rate and Cap to charge the fixed fee.
            </div>
          )}
          <div style={{ fontSize: '.65rem', color: 'var(--hp-dim)', marginTop: 6 }}>
            Fixed fee only → leave Lease Rate and Cap blank. The fee is deducted before GST.
          </div>
        </div>

        {/* Commencement date (read-only info) */}
        {driver.commencementDate && (
          <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--hp-surface)', borderRadius: 8, fontSize: '.8rem' }}>
            <span style={{ color: 'var(--hp-dim)' }}>Commenced: </span>
            <span style={{ fontFamily: 'var(--font-mono)' }}>{driver.commencementDate}</span>
          </div>
        )}

      </div>
    </div>
  );
}

// ─── GST Compliance Letter ──────────────────────────────────────────────────

const HP = { name: 'Hollypaul Recruitment Pty Ltd', abn: '60 651 664 885', address: 'PO Box 1710, Warriewood NSW 2102', email: 'contact@hollypaul.com' };

function GstLetterModal({ driver, onClose }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [filing, setFiling] = useState(false);
  const [filed, setFiled] = useState(false);

  const today = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });

  const handlePrint = () => {
    // Get the HP logo from the page
    const logoImg = document.querySelector('img[alt*="HPR"]') || document.querySelector('[alt*="logo"]');
    const logoDataUrl = logoImg ? logoImg.src : '';

    // Generate the comprehensive letter content
    const fullLetter = gstLetterText(driver, today);

    // Format the letter with proper HTML structure
    const letterHtml = fullLetter.split('\n\n').map(paragraph => {
      // Check for section headers
      if (paragraph === 'Your Legal Obligations') {
        return `<h2 style="font-size: 14px; font-weight: bold; margin-top: 20px; margin-bottom: 12px; color: #1a1a1a;">${paragraph}</h2>`;
      } else if (paragraph === 'What This Means for Payments You Have Received') {
        return `<h2 style="font-size: 14px; font-weight: bold; margin-top: 20px; margin-bottom: 12px; color: #1a1a1a;">${paragraph}</h2>`;
      } else if (paragraph === 'Recommended Next Steps') {
        return `<h2 style="font-size: 14px; font-weight: bold; margin-top: 20px; margin-bottom: 12px; color: #1a1a1a;">${paragraph}</h2>`;
      }
      // Check for bullet points and numbered lists
      else if (paragraph.includes('• ')) {
        const bullets = paragraph.split('\n').filter(line => line.trim().startsWith('•')).map(line => `<li>${line.replace('• ', '')}</li>`).join('');
        return `<ul style="margin: 12px 0 12px 30px;">${bullets}</ul>`;
      } else if (paragraph.match(/^\d+\./)) {
        const items = paragraph.split('\n').filter(line => line.trim().match(/^\d+\./)).map(line => `<li>${line.replace(/^\d+\.\s*/, '')}</li>`).join('');
        return `<ol style="margin: 12px 0 12px 30px;">${items}</ol>`;
      } else {
        return `<p style="margin: 12px 0; text-align: justify;">${paragraph}</p>`;
      }
    }).join('');

    const printWindow = window.open('', '', 'width=900,height=1000');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>GST Compliance Letter</title>
          <style>
            * { margin: 0; padding: 0; }
            body { font-family: Arial, sans-serif; padding: 40px; line-height: 1.6; color: #222; background: white; }
            .letterhead { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; padding-bottom: 20px; border-bottom: 2px solid #c9a84c; }
            .letterhead-left { display: flex; gap: 15px; align-items: flex-start; }
            .letterhead-logo { width: 50px; height: 50px; }
            .letterhead-text h1 { font-size: 18px; font-weight: bold; color: #1a1a1a; margin: 0; }
            .letterhead-text p { font-size: 10px; color: #666; margin: 2px 0; letter-spacing: 1px; text-transform: uppercase; }
            .letterhead-right { text-align: right; font-size: 11px; color: #555; }
            .letterhead-right div { margin: 4px 0; }
            .recipient { margin-bottom: 28px; font-size: 12px; }
            .recipient div { margin: 6px 0; }
            .date { margin-bottom: 18px; font-size: 12px; color: #555; }
            .subject { margin-bottom: 20px; font-size: 13px; font-weight: bold; }
            .body { font-size: 12px; line-height: 1.8; color: #222; }
            .body h2 { font-size: 13px; font-weight: bold; margin-top: 16px; margin-bottom: 10px; color: #1a1a1a; }
            .body p { margin: 10px 0; text-align: justify; }
            .body ul, .body ol { margin: 10px 0 10px 25px; }
            .body li { margin: 6px 0; }
            .signature { margin-top: 40px; }
            .signature-line { border-top: 1px solid #ccc; width: 120px; margin-bottom: 8px; }
            .signature-name { font-weight: bold; margin-top: 16px; }
            .signature-title { font-size: 11px; color: #555; margin-top: 2px; }
          </style>
        </head>
        <body>
          <div class="letterhead">
            <div style="width: 100%; text-align: center; margin-bottom: 20px;">
              ${logoDataUrl ? `<img src="${logoDataUrl}" class="letterhead-logo" alt="HPR Logo" style="height: 60px; width: auto; margin-bottom: 12px;">` : ''}
              <h1 style="font-size: 20px; font-weight: bold; color: #1a1a1a; margin: 0;">HOLLYPAUL</h1>
              <p style="font-size: 10px; color: #666; margin: 2px 0; letter-spacing: 1px; text-transform: uppercase;">Recruitment Pty Ltd</p>
            </div>
            <div style="display: flex; justify-content: flex-end; padding-bottom: 20px; border-bottom: 2px solid #c9a84c;">
              <div style="text-align: right; font-size: 11px; color: #555;">
                <div>PO Box 1710, Warriewood NSW 2102</div>
                <div>ABN: 60 651 664 885</div>
                <div>paul@hollypaul.com</div>
              </div>
            </div>
          </div>

          <div class="recipient">
            <div><strong>${driver.name}</strong></div>
            <div>${driver.companyName || ''}</div>
            <div>${driver.abn ? 'ABN: ' + driver.abn : ''}</div>
            <div>${driver.address || ''}</div>
          </div>

          <div class="date">${today}</div>

          <div class="subject">RE: GST Registration Status — Action Required</div>

          <div class="body">${letterHtml}</div>

          <div class="signature">
            <div class="signature-line"></div>
            <p>Yours sincerely,</p>
            <div class="signature-name">Paul Bouveng</div>
            <div class="signature-title">Director, Hollypaul Recruitment Pty Ltd</div>
          </div>

          <script>window.print();</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // File a copy on the engine. Print and Email both leave NOTHING behind — the letter
  // existed only as a print window or an outbound attachment, so "we sent him a letter"
  // was never provable after the fact. This stores the same text as a branded, signed PDF
  // against the driver (driver-docs-sb assignLetter → private driver-docs bucket), which
  // is also what the driver's My Docs tab reads. Filing does not send anything.
  const handleFile = async () => {
    setFiling(true);
    try {
      const res = await fetch('/.netlify/functions/driver-docs-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'assignLetter', driverLogin: driver.loginName || driver.login,
          category: 'Personal', title: `GST Registration Status — ${today}`,
          bodyText: gstLetterText(driver, today),
        }),
      }).then(r => r.json());
      if (!res.success) throw new Error(res.error || 'unknown error');
      setFiled(true);
    } catch (e) {
      alert(`Could not file the letter: ${e.message}`);
    } finally { setFiling(false); }
  };

  const handleSend = async () => {
    if (!driver.email) { alert('No email address on file for this driver.'); return; }
    setSending(true);
    try {
      // Generate timestamp for PDF filename
      const timestamp = new Date().toISOString().replace(/[^0-9]/g, '').slice(0, 14);
      const fileName = `GST-Letter-${driver.login || 'driver'}-${timestamp}.pdf`;

      // Generate letter content and send email with attached PDF
      // The Netlify function generates the PDF server-side from letterContent
      const letterContent = gstLetterText(driver, today);

      const emailRes = await fetch('/.netlify/functions/appsscript-email', {
        method: 'POST',
        body: JSON.stringify({
          recipientEmail: driver.email,
          driver: driver.name || driver.login,
          letterContent: letterContent,
          fileName: fileName,
          recipientName: driver.name || driver.login,
          recipientCompany: driver.companyName,
          recipientABN: driver.abn,
          recipientAddress: driver.address,
        }),
      });

      const emailData = await emailRes.json();
      if (emailData.success) { setSent(true); }
      else { alert('Send failed: ' + (emailData.error || 'unknown error')); }
    } catch (e) { alert('Send failed: ' + e.message); }
    finally { setSending(false); }
  };

  return (
    <>
      {/* Print-only styles */}
      <style>{`
        @media print {
          * { margin: 0 !important; padding: 0 !important; }
          body { margin: 0 !important; padding: 0 !important; background: white !important; }
          body > *:not(#gst-letter-print) { display: none !important; }
          #gst-letter-print {
            position: static !important;
            inset: auto !important;
            background: white !important;
            display: block !important;
            padding: 0 !important;
            width: 100% !important;
            height: auto !important;
            overflow: visible !important;
          }
          #gst-letter-print > div { width: 100% !important; max-width: 100% !important; }
          .no-print { display: none !important; }
          p { page-break-inside: avoid; }
        }
      `}</style>

      <div id="gst-letter-print" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 2000, overflowY: 'auto', display: 'flex', justifyContent: 'center', paddingTop: 40, paddingBottom: 40 }}>
        <div style={{ width: 720, background: 'white', borderRadius: 8, color: '#111', fontFamily: 'Georgia, serif', fontSize: '14px', lineHeight: 1.7 }}>

          {/* Action bar */}
          <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 24px', background: '#1a1a1a', borderRadius: '8px 8px 0 0' }}>
            <span style={{ color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)', fontSize: '.75rem', textTransform: 'uppercase', letterSpacing: '.1em' }}>GST Compliance Letter</span>
            <div style={{ display: 'flex', gap: 10 }}>
              {filed
                ? <span style={{ color: '#4caf50', fontSize: '.8rem' }}>✓ On record</span>
                : <button className="btn btn-secondary" style={{ fontSize: '.8rem' }} onClick={handleFile} disabled={filing} title="Store a copy against this driver on the engine. Sends nothing.">{filing ? 'Filing…' : 'File on record'}</button>
              }
              {sent
                ? <span style={{ color: '#4caf50', fontSize: '.8rem' }}>✓ Sent to {driver.email}</span>
                : <button className="btn btn-secondary" style={{ fontSize: '.8rem' }} onClick={handleSend} disabled={sending}>{sending ? 'Sending…' : `Email to Driver`}</button>
              }
              <button className="btn btn-primary" style={{ fontSize: '.8rem' }} onClick={handlePrint}>Print / Save PDF</button>
              <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} color="white" /></button>
            </div>
          </div>

          {/* Letter body */}
          <div style={{ padding: '48px 64px' }}>

            {/* Letterhead */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 40, paddingBottom: 20, borderBottom: '2px solid #c9a84c' }}>
              <div>
                <div style={{ fontSize: '22px', fontWeight: 700, fontFamily: 'Arial, sans-serif', color: '#1a1a1a', letterSpacing: '-.3px' }}>HOLLYPAUL</div>
                <div style={{ fontSize: '11px', fontFamily: 'Arial, sans-serif', color: '#666', letterSpacing: '2px', textTransform: 'uppercase' }}>Transport Pty Ltd</div>
              </div>
              <div style={{ textAlign: 'right', fontSize: '12px', color: '#555', fontFamily: 'Arial, sans-serif', lineHeight: 1.8 }}>
                <div>{HP.address}</div>
                <div>ABN: {HP.abn}</div>
                <div>{HP.email}</div>
              </div>
            </div>

            {/* Date & addressee */}
            <div style={{ marginBottom: 28, fontFamily: 'Arial, sans-serif', fontSize: '13px' }}>
              <div style={{ marginBottom: 20, color: '#555' }}>{today}</div>
              <div style={{ fontWeight: 700 }}>{driver.name}</div>
              <div>{driver.companyName}</div>
              <div style={{ color: '#555' }}>ABN: {driver.abn}</div>
              {driver.address && <div style={{ color: '#555' }}>{driver.address}</div>}
            </div>

            {/* Subject line */}
            <div style={{ marginBottom: 24, fontFamily: 'Arial, sans-serif', fontWeight: 700, fontSize: '14px' }}>
              RE: GST Registration Status — Action Required
            </div>

            {/* Body - Dynamic from gstLetterText */}
            <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '13px', lineHeight: 1.85, color: '#222' }}>
              {gstLetterText(driver, today).split('\n\n').map((paragraph, idx) => (
                <p key={idx} style={{ marginBottom: 16 }}>{paragraph}</p>
              ))}
            </div>

          </div>
        </div>
      </div>
    </>
  );
}

function gstLetterText(driver, today) {
  return `GST Registration Status — Action Required\n\nDear ${driver.name.split(' ')[0]},\n\nWe are writing to you following a recent review of our subcontractor records. Our review has identified that GST has been included in payments made to your business by ${HP.name}; however, a check against the Australian Business Register (ABR) indicates that ${driver.companyName || 'your business'} is not currently registered for Goods and Services Tax (GST).\n\nYour Legal Obligations\n\nUnder the A New Tax System (Goods and Services Tax) Act 1999, any business or individual with a GST turnover of $75,000 or more per year is required by law to register for GST. Once this threshold is reached — or expected to be reached — registration must be completed within 21 days.\n\nAs a registered subcontractor engaged in delivery operations, it is highly likely that your annual turnover meets or exceeds this threshold. By not registering for GST, you may be in breach of your obligations under Australian taxation law.\n\nWhat This Means for Payments You Have Received\n\nAll payments made to you by ${HP.name} have included a GST component. Under the law, this GST amount is held by you on behalf of the Australian Taxation Office (ATO) and must be remitted to the ATO — regardless of whether you are currently registered. This means:\n\n• All GST included in payments received from us since the commencement of your engagement must be accounted for and forwarded to the ATO.\n• You are required to lodge Business Activity Statements (BAS) with the ATO for all relevant periods.\n• Failure to remit GST collected may result in penalties and interest charges imposed by the ATO.\n\nWe wish to be clear: this is your legal responsibility to resolve and remedy. ${HP.name} has met its obligations in good faith by including GST in payments to you. The obligation to register, report, and remit GST rests entirely with you.\n\nRecommended Next Steps\n\nWe strongly urge you to take the following steps as a matter of urgency:\n\n1. Contact a registered accountant or tax agent immediately — they can advise you on your specific obligations and help you bring your tax affairs into order.\n2. Register for GST via the ATO's Business Portal at business.gov.au or through your accountant, backdated to when your turnover first exceeded the $75,000 threshold.\n3. Lodge outstanding BAS returns for all periods in which GST was collected.\n4. Remit all GST owing to the ATO, including all GST amounts received from ${HP.name}.\n5. Notify us once your GST registration is confirmed so we can update your records accordingly.\n\nPlease note that failure to address this matter may also affect your ongoing engagement as a subcontractor with us.\n\nWe encourage you to treat this matter with urgency and seek professional advice promptly. If you have any questions regarding this notice, please contact us at ${HP.email}.\n\nYours sincerely,\nPaul Bouveng\n${HP.name}`;
}

// ─── ABN Audit Panel ────────────────────────────────────────────────────────

function AbnAuditPanel({ drivers, onClose, addNotification, onSaved, onEntitiesChanged }) {
  const [results, setResults] = useState([]);
  const [progress, setProgress] = useState(null); // { done, total }
  const [done, setDone] = useState(false);
  const [letterDriver, setLetterDriver] = useState(null);
  const [gstDriver, setGstDriver] = useState(null);

  const driversWithAbn = drivers.filter(d => d.abn && d.abn.replace(/\s/g, '').length >= 11);

  React.useEffect(() => {
    let cancelled = false;
    async function run() {
      const total = driversWithAbn.length;
      setProgress({ done: 0, total });
      const out = [];
      for (let i = 0; i < driversWithAbn.length; i++) {
        if (cancelled) return;
        const d = driversWithAbn[i];
        let row = { driver: d, status: 'checking' };
        try {
          const r = await lookupABN(d.abn);
          if (r.error) {
            row = { driver: d, status: 'error', message: r.error };
          } else {
            const abnActive    = r.status === 'Active';
            const onRecord     = gstOnRecord(d);
            const gstFromAbr   = !!r.gst;
            const gstMismatch  = onRecord !== gstFromAbr;
            const issues = [];
            if (!abnActive) issues.push(`ABN is ${r.status}`);
            if (gstMismatch) issues.push(`GST: app says ${onRecord ? 'Yes' : 'No'}, ABR says ${gstFromAbr ? 'Yes' : 'No'}`);
            row = { driver: d, status: issues.length ? 'issue' : 'ok', issues, abr: r };
            // Persist what this lookup already told us about the trading entity.
            // Best-effort: the audit is about ABN/GST and must not fail over this.
            fetch(ENTITY_URL, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ action: 'saveEntityCheck', login: d.loginName, abn: d.abn, abr: r }),
            }).catch(() => {});
          }
        } catch (e) {
          row = { driver: d, status: 'error', message: e.message };
        }
        out.push(row);
        setResults([...out]);
        setProgress({ done: i + 1, total });
        // brief pause between calls — be kind to the ABR service
        if (i < driversWithAbn.length - 1) await new Promise(r => setTimeout(r, 300));
      }
      if (!cancelled) { setDone(true); onEntitiesChanged && onEntitiesChanged(); }
    }
    run();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  const issues  = results.filter(r => r.status === 'issue');
  const ok      = results.filter(r => r.status === 'ok');
  const errors  = results.filter(r => r.status === 'error');

  return (
    <>
    {letterDriver && <GstLetterModal driver={letterDriver} onClose={() => setLetterDriver(null)} />}
    {gstDriver && (
      <GstCheckModal
        driver={gstDriver}
        addNotification={addNotification}
        onClose={() => setGstDriver(null)}
        onApplied={({ gstRegistered }) => {
          onSaved && onSaved(gstDriver.loginName, { gstRegistered });
          // Re-file this row as resolved rather than leaving a stale ⚠ GST line behind:
          // the audit ran against the pre-fix record and would otherwise still accuse.
          setResults(rs => rs.map(r => {
            if (r.driver.loginName !== gstDriver.loginName) return r;
            const issues = (r.issues || []).filter(i => !i.toLowerCase().includes('gst'));
            const driver = { ...r.driver, gstRegistered };
            return { ...r, driver, issues, status: issues.length ? 'issue' : 'ok' };
          }));
        }}
      />
    )}
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 60, overflowY: 'auto' }}>
      <div style={{ width: 640, background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 12, padding: 32, marginBottom: 40 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.65rem', textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--hp-gold)', marginBottom: 4 }}>ABR Web Service</div>
            <h3 style={{ margin: 0 }}>ABN Audit</h3>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        {/* Progress bar */}
        {progress && !done && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.75rem', color: 'var(--hp-dim)', marginBottom: 6 }}>
              <span>Checking ABNs against ABR…</span>
              <span>{progress.done} / {progress.total}</span>
            </div>
            <div style={{ height: 4, background: 'var(--hp-border)', borderRadius: 2 }}>
              <div style={{ height: '100%', background: 'var(--hp-gold)', borderRadius: 2, width: `${Math.round((progress.done / progress.total) * 100)}%`, transition: 'width .3s' }} />
            </div>
          </div>
        )}

        {/* Summary once done */}
        {done && (
          <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
            {[
              { label: 'Issues found', value: issues.length, color: issues.length ? 'var(--hp-danger)' : 'var(--hp-green)' },
              { label: 'All clear',    value: ok.length,     color: 'var(--hp-green)' },
              { label: 'Errors',       value: errors.length, color: errors.length ? 'var(--hp-amber)' : 'var(--hp-dim)' },
            ].map(s => (
              <div key={s.label} style={{ flex: 1, padding: '12px 16px', background: 'var(--hp-surface)', borderRadius: 8, textAlign: 'center' }}>
                <div style={{ fontSize: '1.6rem', fontWeight: 700, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Results list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Issues first */}
          {issues.map(r => (
            <div key={r.driver.loginName} style={{ padding: '12px 14px', background: 'rgba(220,53,69,0.08)', border: '1px solid rgba(220,53,69,0.25)', borderRadius: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <div style={{ fontWeight: 600 }}>{r.driver.name} <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.72rem', color: 'var(--hp-dim)' }}>{r.driver.loginName}</span></div>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.72rem', color: 'var(--hp-dim)' }}>{r.driver.abn}</span>
              </div>
              <div style={{ fontSize: '.75rem', color: 'var(--hp-dim)', marginBottom: 6 }}>{r.abr?.entityName} · {r.abr?.entityType}</div>
              {r.issues.map((iss, i) => (
                <div key={i} style={{ fontSize: '.78rem', color: 'var(--hp-danger)' }}>⚠ {iss}</div>
              ))}
              {r.issues.some(i => i.toLowerCase().includes('gst')) && (
                <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ fontSize: '.75rem' }}
                    onClick={() => setGstDriver(r.driver)}
                  >
                    <BadgeCheck size={12} /> Fix GST status
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ fontSize: '.75rem' }}
                    onClick={() => setLetterDriver(r.driver)}
                  >
                    Generate GST Compliance Letter
                  </button>
                </div>
              )}
            </div>
          ))}

          {/* Errors */}
          {errors.map(r => (
            <div key={r.driver.loginName} style={{ padding: '12px 14px', background: 'rgba(255,165,0,0.06)', border: '1px solid rgba(255,165,0,0.2)', borderRadius: 8 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{r.driver.name} <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.72rem', color: 'var(--hp-dim)' }}>{r.driver.loginName}</span></div>
              <div style={{ fontSize: '.78rem', color: 'var(--hp-amber)' }}>⚠ Lookup failed: {r.message}</div>
            </div>
          ))}

          {/* OK entries */}
          {done && ok.map(r => (
            <div key={r.driver.loginName} style={{ padding: '10px 14px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span style={{ fontWeight: 600 }}>{r.driver.name}</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.72rem', color: 'var(--hp-dim)', marginLeft: 8 }}>{r.driver.loginName}</span>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: '.72rem', color: 'var(--hp-dim)' }}>{r.abr?.entityName}</span>
                <CheckCircle size={15} color="var(--hp-green)" />
              </div>
            </div>
          ))}

          {/* drivers without ABN */}
          {done && drivers.filter(d => !d.abn || d.abn.replace(/\s/g,'').length < 11).length > 0 && (
            <div style={{ padding: '10px 14px', background: 'var(--hp-surface)', borderRadius: 8, fontSize: '.78rem', color: 'var(--hp-dim)', textAlign: 'center' }}>
              {drivers.filter(d => !d.abn || d.abn.replace(/\s/g,'').length < 11).length} active driver(s) have no ABN on file
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export default function PersonnelPage() {
  const { personnel, setPersonnel, reloadPersonnel, vehicles, addNotification, currentUser, applicantInfoAlertCount } = useApp();
  const navigate = useNavigate();
  // ?tab=applicants&applicant=<id> — the way back from an onboarding task to the
  // interview record it came from.
  const [tab, setTab] = useState(() =>
    (new URLSearchParams(window.location.search).get('tab') === 'applicants' ? 'applicants' : 'drivers'));
  const [photos, setPhotos] = useState({});
  const [filter, setFilter] = useState('active');
  // ?q= deep link from the global search palette pre-fills the search box.
  const [searchParams] = useSearchParams();
  const qParam = searchParams.get('q') || '';
  // The sidebar link carries ?tab=applicants while applicants are waiting. Follow it even
  // when the page is already open (on Drivers), since that doesn't remount it.
  const tabParam = searchParams.get('tab');
  useEffect(() => { if (tabParam === 'applicants') setTab('applicants'); }, [tabParam]);
  const [search, setSearch] = useState(qParam);
  useEffect(() => { if (qParam) setSearch(qParam); }, [qParam]);
  const [showAddDriver, setShowAddDriver] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showAbnAudit, setShowAbnAudit] = useState(false);
  // Trading entity per driver (sole trader / company / no ABN), from entity-status-sb.
  const [entities, setEntities] = useState({});
  const loadEntities = useCallback(async () => {
    try {
      const d = await fetch(ENTITY_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getEntityStatus' }),
      }).then(r => r.json());
      if (d && d.success) setEntities(d.entities || {});
    } catch (_) { /* the chip degrades to "not checked" — never blocks the page */ }
  }, []);
  useEffect(() => { loadEntities(); }, [loadEntities]);
  const [hireApplicant, setHireApplicant] = useState(null);   // applicant being migrated → Drivers
  const [hireSorter, setHireSorter] = useState(null);         // applicant being migrated → night sorters
  const [applicantsReload, setApplicantsReload] = useState(0); // bump to refresh ApplicantsView

  // Applicants → Onboarding. Creates the eight-step task for this person and opens it.
  // Deliberately available before they are hired: creating the driver record is step 1
  // of that task, not a prerequisite for it. An existing task is opened, not duplicated
  // (the backend dedupes on applicantId), so the button is safe to press twice.
  const startOnboarding = useCallback(async (applicant, existing) => {
    if (existing) { navigate('/onboarding'); return; }
    try {
      const res = await fetch('/.netlify/functions/onboarding-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'createTask',
          personName: applicant.fullName || '',
          applicantId: applicant.id || '',
          driverLogin: applicant.driverLogin || '',   // already hired? key it straight away
          assignedTo: 'BOUVENGJ',
        }),
      });
      const d = await res.json();
      if (!d.success) { addNotification(d.error || 'Could not start the onboarding', 'error'); return; }
      addNotification(d.existed
        ? `${applicant.fullName} already has an onboarding task — opening it`
        : `Onboarding set for ${applicant.fullName} — Jayda has it to accept`, 'success');
      navigate('/onboarding');
    } catch (e) { addNotification(e.message, 'error'); }
  }, [addNotification, navigate]);

  // Live contract registers (subcontractor agreements + HPL vehicle leases), pulled
  // once so the table/KPIs reflect who has actually e-signed — the Drivers sheet's
  // own contractSigned flag is hardcoded false and never updated.
  const [agreements, setAgreements] = useState([]);
  const [leases, setLeases] = useState([]);
  const [contractsLoading, setContractsLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setContractsLoading(true);
    Promise.all([
      fetch(AGREEMENT_URL, { method: 'POST', body: JSON.stringify({ action: 'getAgreements' }) }).then(r => r.json()).catch(() => null),
      fetch(CONTRACT_URL,  { method: 'POST', body: JSON.stringify({ action: 'getContracts' }) }).then(r => r.json()).catch(() => null),
    ]).then(([ag, lc]) => {
      if (!alive) return;
      if (ag && ag.success && Array.isArray(ag.agreements)) setAgreements(ag.agreements);
      if (lc && lc.success && Array.isArray(lc.contracts)) setLeases(lc.contracts);
      setContractsLoading(false);
    });
    return () => { alive = false; };
  }, []);

  const findPerson = (login) => personnel.find(p => (p.loginName || '').toUpperCase() === (login || '').toUpperCase()) || null;
  // Jump from "already a driver" to that driver's row: reload first (he may have been
  // created on another page since this list loaded), then search for him across All.
  const openExisting = (login) => {
    reloadPersonnel();
    setTab('drivers');
    setFilter('all');
    setSearch(login);
  };

  const subconByLogin = React.useMemo(() => bestContractByLogin(agreements), [agreements]);
  const leaseByLogin  = React.useMemo(() => bestContractByLogin(leases), [leases]);

  // After the driver is created, mark the applicant Hired (green) + reload the
  // list. Inspect the response so a failed/duplicate mark isn't reported as ok.
  const handleHired = async (login) => {
    if (!hireApplicant) return;
    try {
      const res = await fetch('/.netlify/functions/applicants-sb', {
        method: 'POST',
        body: JSON.stringify({ action: 'saveApplicant', id: hireApplicant.id, markHired: true, driverLogin: login }),
      });
      const data = await res.json();
      if (data.success && data.alreadyHired) {
        addNotification(`Heads up: ${hireApplicant.fullName} was already hired as ${data.existingDriverLogin || 'a driver'} — driver ${login} was still created. Check for a duplicate.`, 'error');
      } else if (data.success) {
        addNotification(`${hireApplicant.fullName} marked as hired`, 'success');
        // Seed the new driver's Compliance (DMS) from what they supplied on the
        // onboarding form — passport / workers-comp photos + emergency contact,
        // address and visa details — so the office doesn't re-collect them.
        try {
          const impRes = await fetch('/.netlify/functions/driver-dms-sb', {
            method: 'POST',
            body: JSON.stringify({ action: 'importFromApplicant', applicantId: hireApplicant.id, driverLogin: login }),
          });
          const imp = await impRes.json();
          if (imp.success && (imp.imported || []).length) {
            addNotification(`Compliance seeded from onboarding: ${imp.imported.join(', ')}`, 'success');
          } else if (!imp.success) {
            addNotification(`Driver created, but seeding Compliance from onboarding failed: ${imp.error || 'unknown error'}`, 'error');
          }
        } catch (impErr) {
          addNotification(`Driver created, but seeding Compliance from onboarding failed: ${impErr.message}`, 'error');
        }
      } else {
        addNotification(`Driver ${login} created, but marking the applicant hired failed: ${data.error || 'unknown error'}. Re-open the applicant and Hire again.`, 'error');
      }
    } catch (err) {
      addNotification(`Driver created, but updating the applicant failed: ${err.message}`, 'error');
    } finally {
      setApplicantsReload(n => n + 1);
      reloadPersonnel();
    }
  };

  // Same close-out for the night-sort road: the sorter row is already created, so
  // stamp the applicant Hired against the SORTER login. A failure here leaves a real
  // sorter with an un-flagged applicant card, so it's reported loudly, not swallowed.
  const handleHiredSorter = async (login) => {
    if (!hireSorter) return;
    try {
      const res = await fetch('/.netlify/functions/applicants-sb', {
        method: 'POST',
        body: JSON.stringify({ action: 'saveApplicant', id: hireSorter.id, markHired: true, hiredAs: 'sorter', sorterLogin: login }),
      });
      const data = await res.json();
      if (data.success && data.alreadyHired) {
        addNotification(`Heads up: ${hireSorter.fullName} was already hired as ${data.existingDriverLogin || 'someone'} — sorter ${login} was still created. Check for a duplicate.`, 'error');
      } else if (data.success) {
        addNotification(`${hireSorter.fullName} added to the night sorters as ${login}`, 'success');
        // A VEVO check (and consent) from their applicant days follows them onto their
        // compliance file — drivers get this through importFromApplicant.
        try {
          const cr = await (await fetch('/.netlify/functions/driver-dms-sb', {
            method: 'POST', body: JSON.stringify({ action: 'carryApplicantVevo', applicantId: hireSorter.id, login }),
          })).json();
          if (cr.success && cr.carried) addNotification(`${login}'s VEVO check and consent copied to Compliance`, 'success');
          else if (!cr.success) addNotification(`Couldn't copy the applicant's VEVO check to ${login}: ${cr.error || 'unknown error'}`, 'error');
        } catch (e) { addNotification(`Couldn't copy the applicant's VEVO check to ${login}: ${e.message}`, 'error'); }
        // The applicant card can only say "Night Sorters" if the hired_as/sorter_login
        // columns exist — flag it rather than let the card claim they went to Drivers.
        if ((data.droppedColumns || []).length) {
          addNotification(`${login} is on the sorters registry, but the applicant card can't record which register — run the pending migration (applicants.hired_as / sorter_login).`, 'error');
        }
      } else {
        addNotification(`Sorter ${login} created, but marking the applicant hired failed: ${data.error || 'unknown error'}. Re-open the applicant and hire again.`, 'error');
      }
    } catch (err) {
      addNotification(`Sorter created, but updating the applicant failed: ${err.message}`, 'error');
    } finally {
      setApplicantsReload(n => n + 1);
    }
  };

  const drivers = personnel.filter(p => p.type !== 'admin');
  const activeDrivers = drivers.filter(d => d.status === 'active');

  // Contract status is derived live from the registers, keyed by login.
  const isSubconSigned = (p) => subconByLogin[(p.loginName || '').toUpperCase()]?.status === 'signed';
  const isLeaseSigned  = (p) => leaseByLogin[(p.loginName || '').toUpperCase()]?.status === 'signed';
  const leaseApplies   = (p) => {
    const veh = vehicleForDriverLogin(p.loginName, vehicles);
    if (!veh) return false;                                  // no van assigned → no lease expected
    return (OWNER_SHORT[veh.owner] || veh.owner) !== 'O/D';  // owner-drivers supply their own van
  };

  const signedCount = activeDrivers.filter(isSubconSigned).length;
  const compliancePct = activeDrivers.length > 0 ? Math.round((signedCount / activeDrivers.length) * 100) : 0;
  const leaseApplicable = activeDrivers.filter(leaseApplies);
  const leaseSignedCount = leaseApplicable.filter(isLeaseSigned).length;

  const filtered = personnel.filter(p => {
    const isActive = p.status === 'active';
    if (filter === 'active' && !isActive) return false;
    if (filter === 'inactive' && isActive) return false;
    if (filter === 'unsigned' && (p.type === 'admin' || isSubconSigned(p))) return false;
    // Unsigned vehicle lease = a driver whose van needs an HPL lease (not owner-driver,
    // van assigned) that isn't signed yet. Owner-drivers / no-van drivers are excluded.
    if (filter === 'unsignedLease' && (p.type === 'admin' || !leaseApplies(p) || isLeaseSigned(p))) return false;
    // Both entity filters are ACTIVE-only. A leaver with no ABN is history, not work —
    // listing them buries the handful of people the number is actually about.
    if (filter === 'soleTrader' && (!isActive || p.type === 'admin' || entityFor(p, entities).kind !== 'sole_trader')) return false;
    // No ABN is never tolerated: the RCTI needs one.
    if (filter === 'noAbn' && (!isActive || p.type === 'admin' || entityFor(p, entities).kind !== 'none')) return false;
    // Every word has to match somewhere, in any order — "Luis Medina" finds
    // "Luis Eduardo Medina Castaneda". A whole-string match missed anyone with a middle name.
    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return true;
    const hay = [p.loginName, p.name, p.companyName, p.email, p.abn, (p.abn || '').replace(/\s/g, '')]
      .map(v => (v || '').toLowerCase()).join(' ');
    return words.every(w => hay.includes(w));
  });

  useEffect(() => {
    const logins = [...new Set((personnel || []).map(x => (x.loginName || x.login || '').toUpperCase()).filter(Boolean))];
    if (logins.length) fetchDriverPhotos(logins).then(p => setPhotos(prev => ({ ...p, ...prev })));
  }, [personnel]);

  return (
    <>
      {showAbnAudit && (
        <AbnAuditPanel
          drivers={activeDrivers}
          onClose={() => setShowAbnAudit(false)}
          addNotification={addNotification}
          onEntitiesChanged={loadEntities}
          onSaved={(login, patch) => {
            const key = (login || '').toUpperCase();
            setPersonnel(prev => prev.map(p => ((p.loginName || '').toUpperCase() === key ? { ...p, ...patch } : p)));
          }}
        />
      )}
      {showAddDriver && (
        <AddDriverPanel
          onClose={() => setShowAddDriver(false)}
          onSuccess={() => reloadPersonnel()}
          addNotification={addNotification}
          findPerson={findPerson}
          onOpenExisting={openExisting}
        />
      )}
      {hireApplicant && (
        <AddDriverPanel
          initial={(() => {
            const o = hireApplicant.onboarding || {};
            return {
              // Login starts blank: the office types the one AP issued. A name-derived guess
              // (SURNAME + initial) got Silvia Patino Ayala hired as AYALAS, not PATINOAYS.
              login: '', name: hireApplicant.fullName,
              mobile: hireApplicant.mobile, email: hireApplicant.email,
              ...(o.sameAddress === false && o.currentAddress ? { address: o.currentAddress } : {}),
              ...(o.accountName ? { accountName: o.accountName } : {}),
              ...(o.bsb ? { bsb: o.bsb } : {}),
              ...(o.accNumber ? { accNumber: o.accNumber } : {}),
              ...(o.gstRegistered ? { gstRegistered: o.gstRegistered === 'yes' ? 'Yes' : 'No' } : {}),
            };
          })()}
          onClose={() => setHireApplicant(null)}
          onSuccess={(login) => handleHired(login)}
          addNotification={addNotification}
          findPerson={findPerson}
          onUseExisting={(login) => handleHired(login)}
          onOpenExisting={openExisting}
        />
      )}
      {hireSorter && (
        <HireSorterPanel
          applicant={hireSorter}
          onClose={() => setHireSorter(null)}
          onSuccess={(login) => handleHiredSorter(login)}
        />
      )}
      {selectedDriver && (
        <DriverProfilePanel
          driver={selectedDriver}
          entity={entityFor(selectedDriver, entities)}
          onEntityChanged={loadEntities}
          subcon={subconByLogin[(selectedDriver.loginName || '').toUpperCase()]}
          lease={leaseByLogin[(selectedDriver.loginName || '').toUpperCase()]}
          vehicle={vehicleForDriverLogin(selectedDriver.loginName, vehicles)}
          contractsLoading={contractsLoading}
          onClose={() => setSelectedDriver(null)}
          addNotification={addNotification}
          onSaved={(patch) => {
            const key = (selectedDriver.loginName || '').toUpperCase();
            setSelectedDriver(prev => (prev ? { ...prev, ...patch } : prev));        // refresh the open panel header
            setPersonnel(prev => prev.map(p =>                                         // refresh the list + reopen
              (p.loginName || '').toUpperCase() === key ? { ...p, ...patch } : p));
          }}
        />
      )}

      <div className="page-header">
        <div className="page-header-left">
          <h2>Personnel</h2>
          <p>{activeDrivers.length} active drivers · {drivers.length - activeDrivers.length} inactive · {personnel.filter(p => p.type === 'admin').length} admin</p>
        </div>
        {tab === 'drivers' && (
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-secondary" onClick={() => setShowAbnAudit(true)}>
              Check All ABNs
            </button>
            <button className="btn btn-primary" onClick={() => setShowAddDriver(true)}>
              <Plus size={14} /> Add Driver
            </button>
          </div>
        )}
      </div>

      <div className="page-body">
        {/* Tabs */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 24, borderBottom: '1px solid var(--hp-border)' }}>
          {[
            { id: 'drivers', label: 'Drivers' },
            { id: 'applicants', label: 'Applicants' },
          ].map(t => (
            <button
              key={t.id}
              // Leaving Applicants drops ?tab=applicants, so the sidebar link can bring you back to it.
              onClick={() => { setTab(t.id); if (t.id !== 'applicants' && tabParam === 'applicants') navigate('/personnel', { replace: true }); }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: '10px 14px',
                fontSize: '.85rem', fontWeight: 600, fontFamily: 'var(--font-body)',
                color: tab === t.id ? 'var(--hp-gold)' : 'var(--hp-dim)',
                borderBottom: tab === t.id ? '2px solid var(--hp-gold)' : '2px solid transparent',
                marginBottom: -1,
              }}
            >
              {t.label}
              {t.id === 'applicants' && applicantInfoAlertCount > 0 && (
                <span title={`${applicantInfoAlertCount} applicant${applicantInfoAlertCount > 1 ? 's' : ''} waiting to be reviewed`}
                  style={{ marginLeft: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 5px', borderRadius: 999, background: '#ef4444', color: '#fff', fontSize: '.66rem', fontWeight: 700, verticalAlign: 'middle' }}>
                  {applicantInfoAlertCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {tab === 'applicants' ? (
          <ApplicantsView addNotification={addNotification} recordedBy={currentUser?.login || ''}
            onHire={setHireApplicant} onHireSorter={setHireSorter} onStartOnboarding={startOnboarding}
            initialApplicantId={searchParams.get('applicant') || ''}
            reloadSignal={applicantsReload} />
        ) : (
        <>
        {/* Stats */}
        <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: '24px' }}>
          {[
            { label: 'Active Drivers', value: activeDrivers.length, accent: 'var(--hp-green)' },
            { label: 'Subcontractor Signed', value: `${signedCount}/${activeDrivers.length}`, accent: signedCount < activeDrivers.length ? 'var(--hp-amber)' : 'var(--hp-green)' },
            { label: 'Vehicle Leases Signed', value: `${leaseSignedCount}/${leaseApplicable.length}`, accent: leaseSignedCount < leaseApplicable.length ? 'var(--hp-amber)' : 'var(--hp-green)' },
            { label: 'Subcontractor Compliance', value: `${compliancePct}%`, accent: compliancePct === 100 ? 'var(--hp-green)' : 'var(--hp-amber)' },
          ].map(s => (
            <div className="stat-card" key={s.label} style={{ '--accent': s.accent }}>
              <div className="stat-label">{s.label}</div>
              <div className="stat-value" style={{ fontSize: '2rem' }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Compliance bar */}
        <div className="card" style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Subcontractor Agreement Compliance</span>
            <span style={{ fontSize: '0.8rem', color: 'var(--hp-dim)' }}>{signedCount} of {activeDrivers.length} signed</span>
          </div>
          <div className="compliance-bar">
            <div className="compliance-fill" style={{ width: `${compliancePct}%` }} />
          </div>
          {signedCount < activeDrivers.length && (
            <div style={{ marginTop: '10px', fontSize: '0.78rem', color: 'var(--hp-amber)' }}>
              ⚠ {activeDrivers.length - signedCount} driver(s) have not signed their subcontractor agreement
            </div>
          )}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            className="form-input"
            style={{ maxWidth: '280px' }}
            placeholder="Search login, name, company, ABN…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div style={{ display: 'flex', gap: '6px' }}>
            {[
              { id: 'active', label: 'Active' },
              { id: 'inactive', label: 'Inactive' },
              { id: 'all', label: 'All' },
              { id: 'unsigned', label: 'Unsigned contracts' },
              { id: 'unsignedLease', label: 'Unsigned vehicle lease' },
              { id: 'soleTrader', label: 'Sole traders' },
              { id: 'noAbn', label: 'No ABN' },
            ].map(f => (
              <button key={f.id} className={`btn btn-sm ${filter === f.id ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter(f.id)}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 120 }}>Login</th>
                <th>Name</th>
                <th>Company</th>
                <th style={{ width: 90 }}>Type</th>
                <th style={{ width: 90 }}>Status</th>
                <th style={{ width: 110 }}>Subcontractor</th>
                <th style={{ width: 250 }}>Vehicle Lease</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', color: 'var(--hp-muted)', padding: '32px 0', fontSize: '.85rem' }}>
                    No personnel match your search
                  </td>
                </tr>
              )}
              {filtered.map(p => (
                <tr
                  key={p.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedDriver(p)}
                >
                  {/* Login */}
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <DriverAvatar login={p.loginName} photo={photos[(p.loginName || '').toUpperCase()]} size={30} />
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.78rem', fontWeight: 600, color: 'var(--hp-gold)' }}>
                        {p.loginName || '—'}
                      </span>
                    </div>
                  </td>

                  {/* Name */}
                  <td>
                    <span style={{ fontWeight: 500, fontSize: '.88rem' }}>{p.name || '—'}</span>
                  </td>

                  {/* Company / trading entity — the company NAME when there is one,
                      otherwise what they actually trade as. "—" told us nothing. */}
                  <td>
                    {p.companyName ? (
                      <span style={{ fontSize: '.85rem', color: 'var(--hp-text)' }}>{p.companyName}</span>
                    ) : (() => {
                      const e = entityFor(p, entities);
                      const look = ENTITY_LOOK[e.kind] || ENTITY_LOOK.unknown;
                      return (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span className={`badge ${look.cls}`}>{look.label}</span>
                          {e.companyPending && (
                            <span style={{ fontSize: '.7rem', color: 'var(--hp-amber, #b45309)' }}>
                              company on the way{e.companyExpectedBy ? ` · ${e.companyExpectedBy}` : ''}
                            </span>
                          )}
                        </span>
                      );
                    })()}
                  </td>

                  {/* Type */}
                  <td>
                    <span className={`badge ${(p.type || '').trim() === 'Hourly' ? 'badge-blue' : (p.type || '').trim() === 'Parcel' ? 'badge-green' : 'badge-dim'}`}>
                      {(p.type || 'None').trim()}
                    </span>
                  </td>

                  {/* Status */}
                  <td>
                    <span className={`badge ${p.status === 'active' ? 'badge-green' : 'badge-dim'}`} style={{ textTransform: 'capitalize' }}>
                      {p.status}
                    </span>
                  </td>

                  {/* Subcontractor agreement (from the live register) */}
                  <td>
                    {p.type === 'admin'
                      ? <span style={{ color: 'var(--hp-muted)', fontSize: '0.8rem' }}>N/A</span>
                      : (() => {
                          const c = subconByLogin[(p.loginName || '').toUpperCase()];
                          return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <ContractStatusBadge contract={c} loading={contractsLoading} />
                              {!contractsLoading && needsGenerate(c) && (
                                <GenerateContractButton label={c ? 'Re-issue' : 'Generate'} onClick={(e) => { e.stopPropagation(); navigate(`/contracts?tab=subcontractor&driver=${encodeURIComponent(p.loginName || '')}`); }} />
                              )}
                            </div>
                          );
                        })()
                    }
                  </td>

                  {/* Vehicle lease (HPL-as-Lessor), tagged with the van's owner */}
                  <td>
                    {(() => {
                      if (p.type === 'admin') return <span style={{ color: 'var(--hp-muted)', fontSize: '0.8rem' }}>N/A</span>;
                      const veh = vehicleForDriverLogin(p.loginName, vehicles);
                      const ownerShort = veh ? (OWNER_SHORT[veh.owner] || veh.owner || '') : '';
                      const lease = leaseByLogin[(p.loginName || '').toUpperCase()];
                      if (!veh) return <span style={{ color: 'var(--hp-muted)', fontSize: '0.8rem' }}>No van assigned</span>;
                      if (ownerShort === 'O/D') return <span style={{ color: 'var(--hp-muted)', fontSize: '0.8rem' }}>Owner driver</span>;
                      const badge = OWNER_BADGE[ownerShort] || { bg: 'var(--hp-surface)', color: 'var(--hp-dim)' };
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ background: badge.bg, color: badge.color, fontSize: '0.62rem', fontWeight: 700, padding: '2px 6px', borderRadius: 4, whiteSpace: 'nowrap' }}>{ownerShort || '—'}</span>
                          {veh.rego && <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.68rem', color: 'var(--hp-dim)', whiteSpace: 'nowrap' }}>{veh.rego}</span>}
                          <ContractStatusBadge contract={lease} loading={contractsLoading} />
                          {!contractsLoading && needsGenerate(lease) && (
                            <GenerateContractButton label={lease ? 'Re-issue' : 'Generate'} onClick={(e) => { e.stopPropagation(); navigate(`/contracts?tab=vehicle-lease&driver=${encodeURIComponent(p.loginName || '')}`); }} />
                          )}
                        </div>
                      );
                    })()}
                  </td>

                  {/* Arrow */}
                  <td style={{ textAlign: 'right' }}>
                    <ChevronRight size={15} color="var(--hp-muted)" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
        )}
      </div>
    </>
  );
}
